import {Box, Collapse, createStyles, Flex, Group, Stack, Text, Tooltip} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useMemo, useState} from 'react';

import {MetadataGridContents} from 'analysis-tool/data-view/metadata-modal/ViewMetadataModal';
import {formatCanvasVersionLabel} from 'canvas-core/canvasVersionUtil';
import {CollapsibleTriangle} from 'config-migration/shared-ui-components/TriangleToggle';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';
import {readableTime} from 'util/DateUtil';
import fuzzydate from 'util/fuzzydate';

import type {
  CanvasDefinitionFileStateQuery,
  CanvasDefinitionFileStateQueryVariables,
} from '../backbone/__generated/CanvasDefinitionFileStateQuery';
import type {
  CanvasVersionFileStateQuery,
  CanvasVersionFileStateQueryVariables,
} from '../backbone/__generated/CanvasVersionFileStateQuery';
import {
  CANVAS_DEFINITION_FILE_STATE_QUERY,
  CanvasDefinitionFileStateGraphQL,
} from '../backbone/CanvasDefinitionGraphQL';
import {
  CANVAS_VERSION_FILE_STATE_QUERY,
  CanvasVersionFileStateGraphQL,
} from '../backbone/CanvasVersionGraphQL';
import CanvasPreviewFrame from '../preview/CanvasPreviewFrame';
import useCanvasExpandedPreviewUrl, {
  CanvasExpandedPreviewTarget,
} from '../preview/useCanvasExpandedPreviewUrl';
import CanvasVersionDropdown, {CanvasVersionOption} from './CanvasVersionDropdown';

type CanvasCreator = CanvasDefinitionFileStateGraphQL['creator'] | CanvasVersionFileStateGraphQL['creator'];

const useStyles = createStyles((theme) => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
    overflow: 'auto',
  },
  headerRow: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  iconTile: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[8],
    borderRadius: theme.radius.sm,
    color: theme.white,
    display: 'flex',
    height: 40,
    justifyContent: 'center',
    width: 40,
    flexShrink: 0,
  },
  titleRow: {
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  title: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  subtitle: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
  },
  headerActions: {
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing.sm,
  },
  metadataValue: {
    wordBreak: 'break-all',
  },
  previewSection: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 480,
  },
}));

interface CanvasViewMetadata {
  canvasDefinitionId: string;
  canvasVersionIndex: number | null;
  createdAt: string;
  creator: CanvasCreator;
  description: string | null;
  title: string;
}

function getCreatorName(creator: CanvasCreator | null | undefined): string | null {
  if (creator == null || isInaccessible(creator)) {
    return null;
  }
  return creator.name;
}

function metadataFromDefinition(canvasDefinition: CanvasDefinitionFileStateGraphQL): CanvasViewMetadata {
  return {
    canvasDefinitionId: canvasDefinition.id,
    canvasVersionIndex: canvasDefinition.latestUploadedVersionIndex ?? null,
    createdAt: canvasDefinition.createdAt,
    creator: canvasDefinition.creator,
    description: canvasDefinition.description ?? null,
    title: canvasDefinition.name,
  };
}

function metadataFromVersion(canvasVersion: CanvasVersionFileStateGraphQL): CanvasViewMetadata {
  return {
    canvasDefinitionId: canvasVersion.definition.id,
    canvasVersionIndex: canvasVersion.versionIndex,
    createdAt: canvasVersion.createdAt,
    creator: canvasVersion.creator,
    description: canvasVersion.definition.description ?? null,
    title: canvasVersion.definition.name,
  };
}

function uploadedVersionsFromDefinition(
  canvasDefinition: CanvasDefinitionFileStateGraphQL
): Array<CanvasVersionOption> {
  return canvasDefinition.versions.map((version) => ({
    id: version.id,
    versionIndex: version.versionIndex,
  }));
}

function resolveSelectedVersionId(
  versions: Array<CanvasVersionOption>,
  canvasVersionId: string | null | undefined,
  latestUploadedVersionIndex: number | null
): string | null {
  if (canvasVersionId != null) {
    return canvasVersionId;
  }
  const latestVersion = versions.find((version) => version.versionIndex === latestUploadedVersionIndex);
  return latestVersion?.id ?? versions[0]?.id ?? null;
}

function CanvasExpandedViewContent({
  metadata,
  target,
  versions,
  selectedVersionId,
}: {
  metadata: CanvasViewMetadata;
  target: CanvasExpandedPreviewTarget;
  versions: Array<CanvasVersionOption>;
  selectedVersionId: string | null;
}): JSX.Element {
  const {classes} = useStyles();
  const [refreshToken, setRefreshToken] = useState(0);
  const [detailsOpened, {toggle: toggleDetails}] = useDisclosure(false);
  const {signedUrl, isLoading, errorMessage, sdkVersion} = useCanvasExpandedPreviewUrl(target, refreshToken);

  const creatorName = getCreatorName(metadata.creator);
  const headerCreatedTime = fuzzydate(metadata.createdAt);
  const metadataCreatedTime = readableTime(metadata.createdAt);

  const metadataGridCells = useMemo(
    () => [
      {
        label: 'Description',
        component: metadata.description ?? '—',
      },
      {
        label: 'Canvas ID',
        component: <span className={classes.metadataValue}>{metadata.canvasDefinitionId}</span>,
      },
      {
        label: 'Version',
        component:
          metadata.canvasVersionIndex != null ? formatCanvasVersionLabel(metadata.canvasVersionIndex) : '—',
      },
      {
        label: 'Created at',
        component: metadataCreatedTime,
      },
    ],
    [
      classes.metadataValue,
      metadata.canvasDefinitionId,
      metadata.canvasVersionIndex,
      metadata.description,
      metadataCreatedTime,
    ]
  );

  return (
    <div className={classes.page} {...getTestProps({component: 'CanvasExpandedView', element: 'root'})}>
      <Flex className={classes.headerRow}>
        <Group align="flex-start" spacing="lg" noWrap={true}>
          <div className={classes.iconTile}>
            <BenchlingIcon icon="canvas_definition" />
          </div>
          <Stack spacing="sm">
            <Group align="center" className={classes.titleRow}>
              <Text className={classes.title}>{metadata.title}</Text>
              <CanvasVersionDropdown
                canvasDefinitionId={metadata.canvasDefinitionId}
                versions={versions}
                selectedVersionId={selectedVersionId}
              />
            </Group>
            <Text className={classes.subtitle}>
              Created {headerCreatedTime}
              {creatorName != null && (
                <>
                  {' '}
                  by <strong>{creatorName}</strong>
                </>
              )}
            </Text>
          </Stack>
        </Group>
        <Group className={classes.headerActions} noWrap={true}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            hideDropdownCaret={true}
            onClick={toggleDetails}
            aria-expanded={detailsOpened}
            {...getTestProps({component: 'CanvasExpandedView', element: 'detailsButton'})}
          >
            Details <CollapsibleTriangle isCollapsed={!detailsOpened} />
          </Button>
          <Tooltip label={isLoading ? 'Refreshing canvas...' : 'Refresh canvas'}>
            <span>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                onClick={() => {
                  setRefreshToken((token) => token + 1);
                }}
                disabled={isLoading}
                icon="refresh"
                aria-label="Refresh canvas"
                {...getTestProps({component: 'CanvasExpandedView', element: 'refreshButton'})}
              />
            </span>
          </Tooltip>
        </Group>
      </Flex>

      <Collapse in={detailsOpened}>
        <Box pb="md">
          <MetadataGridContents gridCells={metadataGridCells} />
        </Box>
      </Collapse>

      <div className={classes.previewSection}>
        {isLoading ? (
          <CenteredSpinner />
        ) : errorMessage != null ? (
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        ) : signedUrl != null ? (
          <CanvasPreviewFrame signedUrl={signedUrl} sdkVersion={sdkVersion} />
        ) : (
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the canvas." />
        )}
      </div>
    </div>
  );
}

export default function CanvasExpandedView(props: {
  canvasDefinitionId: string;
  canvasVersionId?: string;
}): JSX.Element {
  if (props.canvasVersionId != null) {
    return <CanvasVersionExpandedView canvasVersionId={props.canvasVersionId} />;
  }
  return <CanvasDefinitionExpandedView canvasDefinitionId={props.canvasDefinitionId} />;
}

function CanvasDefinitionExpandedView({canvasDefinitionId}: {canvasDefinitionId: string}): JSX.Element {
  const queryResult = useBenchlingQuery<
    CanvasDefinitionFileStateQuery,
    CanvasDefinitionFileStateQueryVariables
  >(CANVAS_DEFINITION_FILE_STATE_QUERY, {
    variables: {canvasDefinitionId},
  });

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (queryResult.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const {canvasDefinition} = queryResult.data;
  if (isInaccessible(canvasDefinition)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const versions = uploadedVersionsFromDefinition(canvasDefinition);

  return (
    <CanvasExpandedViewContent
      metadata={metadataFromDefinition(canvasDefinition)}
      target={{canvasDefinitionId}}
      versions={versions}
      selectedVersionId={resolveSelectedVersionId(
        versions,
        null,
        canvasDefinition.latestUploadedVersionIndex
      )}
    />
  );
}

function CanvasVersionExpandedView({canvasVersionId}: {canvasVersionId: string}): JSX.Element {
  const versionQueryResult = useBenchlingQuery<
    CanvasVersionFileStateQuery,
    CanvasVersionFileStateQueryVariables
  >(CANVAS_VERSION_FILE_STATE_QUERY, {
    variables: {canvasVersionId},
  });

  if (versionQueryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (versionQueryResult.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const {canvasVersion} = versionQueryResult.data;
  if (isInaccessible(canvasVersion)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  return <CanvasVersionExpandedViewContent canvasVersion={canvasVersion} canvasVersionId={canvasVersionId} />;
}

function CanvasVersionExpandedViewContent({
  canvasVersion,
  canvasVersionId,
}: {
  canvasVersion: CanvasVersionFileStateGraphQL;
  canvasVersionId: string;
}): JSX.Element {
  const definitionQueryResult = useBenchlingQuery<
    CanvasDefinitionFileStateQuery,
    CanvasDefinitionFileStateQueryVariables
  >(CANVAS_DEFINITION_FILE_STATE_QUERY, {
    variables: {canvasDefinitionId: canvasVersion.definition.id},
  });

  if (definitionQueryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (definitionQueryResult.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const {canvasDefinition} = definitionQueryResult.data;
  if (isInaccessible(canvasDefinition)) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  const versions = uploadedVersionsFromDefinition(canvasDefinition);

  return (
    <CanvasExpandedViewContent
      metadata={metadataFromVersion(canvasVersion)}
      target={{canvasVersionId}}
      versions={versions}
      selectedVersionId={resolveSelectedVersionId(versions, canvasVersionId, canvasVersion.versionIndex)}
    />
  );
}
